import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Plus } from "lucide-react";
import { PageHero } from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { FALLBACK_PACKS } from "../lib/data";

export const PartyPacksPage = () => {
    const [packs, setPacks] = useState(FALLBACK_PACKS);
    const [loaded, setLoaded] = useState(false);
    const { addToCart } = useCart();

    useEffect(() => {
        document.title = "Party Packs — Cotton Candy & Popcorn Packages NYC | Showtime Carnival Co.";
        api.get("/party-packs")
            .then((res) => { setPacks(res.data || []); setLoaded(true); })
            .catch(() => setLoaded(true));
    }, []);

    return (
        <>
            <PageHero
                kicker="Party Ready"
                title="Packs For The Crew."
                copy="Cotton candy + popcorn combinations sized for your guest list. Party packs and pricing are being finalized — check back soon."
                dark={false}
                testid="party-packs-hero"
            />
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid gap-6 md:grid-cols-3" data-testid="party-packs-grid">
                    {loaded && packs.length === 0 && (
                        <div className="col-span-full rounded-3xl border border-dashed border-zinc-300 p-12 text-center" data-testid="party-packs-empty">
                            <p className="font-oswald uppercase text-2xl text-zinc-400">Party packs are being finalized.</p>
                            <p className="text-sm text-zinc-400 mt-2">Packages and pricing go live once approved — check back soon.</p>
                        </div>
                    )}
                    {packs.map((pack, i) => (
                        <Reveal key={pack.id} delay={i * 0.1} className="h-full">
                            <motion.div
                                whileHover={{ y: -8 }}
                                className={`h-full rounded-3xl p-8 flex flex-col ${
                                    pack.featured ? "bg-ink text-white ring-4 ring-pink" : "bg-paper border border-zinc-200"
                                }`}
                                data-testid={`party-packs-card-${pack.id}`}
                            >
                                {pack.featured && (
                                    <span className="self-start mb-4 rounded-full bg-pink px-4 py-1.5 font-oswald text-[10px] tracking-[0.3em] uppercase text-ink">
                                        Most Popular
                                    </span>
                                )}
                                <h2 className="font-oswald uppercase text-3xl">{pack.name}</h2>
                                <p className={`mt-1 text-sm font-semibold ${pack.featured ? "text-pink" : "text-pink-dark"}`}>{pack.guests}</p>
                                <p className="mt-5 font-oswald text-4xl">${pack.price}</p>
                                <ul className="mt-6 space-y-3 flex-1">
                                    {pack.contents.map((item) => (
                                        <li key={item} className="flex items-start gap-3 text-sm">
                                            <Check size={16} className={`mt-0.5 shrink-0 ${pack.featured ? "text-pink" : "text-pink-dark"}`} />
                                            <span className={pack.featured ? "text-zinc-300" : "text-zinc-600"}>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                {pack.status === "SOLD_OUT" ? (
                                    <span
                                        data-testid={`party-pack-soldout-${pack.id}`}
                                        className="mt-8 inline-flex items-center justify-center rounded-full py-3.5 font-oswald uppercase tracking-widest text-sm bg-zinc-200 text-zinc-400 cursor-not-allowed"
                                    >
                                        Sold Out
                                    </span>
                                ) : (
                                    <button
                                        onClick={() => addToCart({ id: pack.id, name: `${pack.name} Party Pack`, price: pack.price, kind: "party_pack" })}
                                        data-testid={`party-pack-add-${pack.id}`}
                                        className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-oswald uppercase tracking-widest text-sm transition-colors duration-300 active:scale-95 ${
                                            pack.featured ? "bg-pink text-ink hover:bg-white" : "bg-ink text-white hover:bg-pink hover:text-ink"
                                        }`}
                                    >
                                        <Plus size={15} /> Add To Cart
                                    </button>
                                )}
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
            </section>
        </>
    );
};
